import { memo, useCallback, useEffect, useRef, useState } from "react";
import { MdEvent, MdCreate, MdPeople, MdLockOpen, MdWarning, MdAgriculture, MdLock, MdPrint } from "react-icons/md";
import { Tooltip } from "antd";
import VirtualList from "rc-virtual-list";
import { ButtonIcon, FlexStyled, MaterialIcons, MetaItem, OperationActions, OperationCheckbox, OperationContent, OperationDetails, OperationItem, OperationMeta, OperationName, OperationNameTitle, OperationTags, PlansList, Status } from '../styles';
import useModalOrdemServicoMatriz from "../../../hooks/useModalOrdemServicoMatriz";
import { dateFormat, getStatus } from "../utils";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import { Loader } from "./Loader";
import { usePrinter } from "../../OrdemServico/hooks/useCreateOrdemServico";

const OS_LIST_ITEM_HEIGHT = 112;
const VIRTUAL_LIST_MIN_ITEMS = 30;

interface OperationCardProps {
  ordemServico: any;
  selectedItem: string | null;
  canVincularOrdemServico: boolean;
  onSelectItem: (ordemServico: any) => void;
  onEdit: () => void;
  onPrinter: () => void;
  activeEdit: boolean;
  setActiveEdit: (activeEdit: boolean) => void;
  setEditChecked: (editChecked: boolean) => void;
}

const OperationCard = memo(function OperationCard({
  ordemServico,
  selectedItem,
  canVincularOrdemServico,
  onSelectItem,
  onEdit,
  onPrinter,
  activeEdit,
  setActiveEdit,
  setEditChecked
}: OperationCardProps) {
  const isSelected = selectedItem === ordemServico.ordemServicoId;
  return (
    <OperationItem hasPlanning={!ordemServico?.possuiPlanoOperacional}>
      <OperationContent>
        <OperationCheckbox>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              onSelectItem(ordemServico);
              setActiveEdit(false);
              setEditChecked(e.target.checked);
            }}
          />
        </OperationCheckbox>
        <OperationDetails>
          <OperationName>
            <FlexStyled>
              <OperationNameTitle>
                {
                  !ordemServico?.possuiPlanoOperacional &&
                  <span>
                    <MdWarning color="#f44336" title="OS sem Planejamento" />
                  </span>
                }
                {ordemServico?.numero}
              </OperationNameTitle>

              <Status status={ordemServico?.situacao}>
                {ordemServico?.situacao === 'A' &&
                  <MdLockOpen size={12} />
                }
                {ordemServico?.situacao === 'F' &&
                  <MdLock size={12} />
                }
                <span>{getStatus(ordemServico?.situacao)}</span>
              </Status>

            </FlexStyled>
            <Tooltip title={!canVincularOrdemServico ? 'Você não tem permissão para vincular ordem de serviço' : undefined}>
              <OperationActions>
                <ButtonIcon
                  disabled={!isSelected || !canVincularOrdemServico}
                  onClick={onPrinter}
                >
                  <MaterialIcons color="#757575"><MdPrint /></MaterialIcons>
                </ButtonIcon>
                <ButtonIcon
                  disabled={!isSelected || !canVincularOrdemServico}
                  onClick={onEdit}
                  activeEdit={activeEdit}
                >
                  <MaterialIcons color="#757575"><MdCreate /></MaterialIcons>
                </ButtonIcon>
              </OperationActions>
            </Tooltip>
          </OperationName>
          <OperationMeta>
            <MetaItem>
              <MaterialIcons color="#757575"><MdEvent /></MaterialIcons>
              <span> {dateFormat(ordemServico?.dataAbertura)}</span>
            </MetaItem>
            <MetaItem>
              <MaterialIcons color="#757575"><MdPeople /></MaterialIcons>
              <span>{ordemServico?.equipe}</span>
            </MetaItem>
          </OperationMeta>
          <OperationTags>
            <span>
              <MdAgriculture color="#1976d2" size={14} />
            </span>
            <span>{ordemServico?.operacao}</span>
          </OperationTags>
        </OperationDetails>
      </OperationContent>
    </OperationItem>
  );
});

interface ListProps {
  listItem: any[];
  dataMapOS?: any;
  isFetching: boolean;
}

function List({ listItem, dataMapOS, isFetching }: ListProps) {
  const {
    ordemServicoIdAndColor,
    setOrdemServicoIdAndColor,
    setOrdemService,
    setIds,
    selectedItem,
    setSelectedItem,
    setActiveEdit,
    setEditChecked,
    activeEdit,
  } = useModalOrdemServicoMatriz();

  const { hasPermission } = useValidatePermission();
  const canVincularOrdemServico = hasPermission(['PlanoOperacional.VincularOrdemServico']);

  const listContainerRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(0);
  const { mutateAsync: exportShedule } = usePrinter();

  useEffect(() => {
    const element = listContainerRef.current;
    if (!element) return;

    const updateHeight = () => {
      setListHeight(element.clientHeight);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleEdit = useCallback(() => {
    if (ordemServicoIdAndColor.talhaoIds) {
      setIds(ordemServicoIdAndColor.talhaoIds);
      setActiveEdit(!activeEdit);
    }
  }, [ordemServicoIdAndColor.talhaoIds, setIds, setActiveEdit, activeEdit]);

  const handlePrinter = useCallback(async (ordemService: any) => {
    try {
      const id = ordemService?.ordemServicoId;
      const blob = await exportShedule(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "arquivo.pdf";
      window.open(url, "_blank");
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (e) {
      console.log(e);

    }
  }, []);

  const handleSelectItem = useCallback((ordemServico: any) => {
    const id = ordemServico.ordemServicoId;
    setSelectedItem((prev) => {
      const newValue = prev === id ? null : id;

      if (newValue) {
        const list = dataMapOS?.features
          ?.filter((f: any) => f?.properties?.ordemServicoIds?.includes(newValue))
          ?.map((f: any) => ({
            talhaoId: f?.properties?.id,
            code: f?.id,
            tamanhoAreaHa: f?.properties?.tamanhoAreaHa
          }));

        const possuiPlanoOperacional = ordemServico?.possuiPlanoOperacional;
        setOrdemServicoIdAndColor({
          ordemServicoId: newValue,
          color: ordemServico?.possuiTalhaoSemPlanoOperacional ? '#fbbf24' : possuiPlanoOperacional ? '#009157' : '#cecece',
          talhaoIds: list?.map((l: any) => l?.talhaoId),
          codesAndArea: list?.map((l: any) => ({ code: l?.code, tamanhoAreaHa: l?.tamanhoAreaHa })),
        });
        setOrdemService({
          code: ordemServico?.numero,
          date: ordemServico?.dataAbertura,
          team: ordemServico?.equipe,
          operation: ordemServico?.operacao,
          possuiPlanoOperacional: ordemServico?.possuiPlanoOperacional
        });
      } else {
        setOrdemServicoIdAndColor({
          ordemServicoId: '',
          color: '',
          talhaoIds: [],
          codesAndArea: []
        });
        setOrdemService({ code: '', date: '', team: '', operation: '', possuiPlanoOperacional: false });
      }

      return newValue;
    });
  }, [dataMapOS?.features, setOrdemServicoIdAndColor, setOrdemService, setSelectedItem]);

  const renderRow = useCallback((ordemServico: any) => (
    <OperationCard
      ordemServico={ordemServico}
      onSelectItem={handleSelectItem}
      onEdit={handleEdit}
      onPrinter={() => handlePrinter(ordemServico)}
      selectedItem={selectedItem}
      canVincularOrdemServico={canVincularOrdemServico}
      activeEdit={activeEdit}
      setActiveEdit={setActiveEdit}
      setEditChecked={setEditChecked}
    />
  ), [canVincularOrdemServico, handleEdit, handleSelectItem, selectedItem, handlePrinter]);

  const shouldVirtualize = (listItem?.length ?? 0) >= VIRTUAL_LIST_MIN_ITEMS;
  const showVirtualList = shouldVirtualize && listHeight > 0;

  return (
    <PlansList ref={listContainerRef} showVirtualList={showVirtualList}>
      {isFetching ? <Loader /> : showVirtualList ? (
        <VirtualList
          data={listItem}
          height={listHeight}
          itemHeight={OS_LIST_ITEM_HEIGHT}
          itemKey={(item) => item.ordemServicoId}
          style={{ padding: "12px" }}
        >
          {renderRow}
        </VirtualList>
      ) : !shouldVirtualize ? (
        listItem?.map((ordemServico) => (
          <OperationCard
            key={ordemServico.ordemServicoId}
            ordemServico={ordemServico}
            onSelectItem={handleSelectItem}
            onEdit={handleEdit}
            onPrinter={() => handlePrinter(ordemServico)}
            selectedItem={selectedItem}
            canVincularOrdemServico={canVincularOrdemServico}
            activeEdit={activeEdit}
            setActiveEdit={setActiveEdit}
            setEditChecked={setEditChecked}
          />
        ))
      ) : null}
    </PlansList>
  );
}

export default List;
